import {
  DocumentModel,
  DocumentBlock,
  BlockType,
  ShapeType,
  TextBlock,
  FormulaBlock,
  TableBlock,
  ImageBlock,
  ShapeBlock,
  PageBreakBlock,
} from '../ast/document.types';

function generateId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

export function createShapeBlock(shapeType: ShapeType = 'rectangle'): ShapeBlock {
  const now = new Date().toISOString();
  const defaultDimensions: Record<ShapeType, { width: number; height: number; strokeWidth: number }> = {
    rectangle: { width: 160, height: 80, strokeWidth: 1.5 },
    rounded_rectangle: { width: 160, height: 80, strokeWidth: 1.5 },
    circle: { width: 90, height: 90, strokeWidth: 1.5 },
    ellipse: { width: 140, height: 80, strokeWidth: 1.5 },
    line: { width: 240, height: 2, strokeWidth: 2 },
    arrow: { width: 200, height: 24, strokeWidth: 2 },
    triangle: { width: 100, height: 90, strokeWidth: 1.5 },
    divider: { width: 450, height: 12, strokeWidth: 1 },
  };

  const dim = defaultDimensions[shapeType] || { width: 140, height: 70, strokeWidth: 1.5 };

  return {
    id: generateId('shp'),
    type: 'shape',
    createdAt: now,
    data: {
      shapeType,
      fill: shapeType === 'divider' || shapeType === 'line' ? 'transparent' : '#f0f4f8',
      stroke: '#1e3a8a',
      strokeWidth: dim.strokeWidth,
      strokeStyle: 'solid',
      opacity: 1,
      width: dim.width,
      height: dim.height,
      rotation: 0,
      alignment: 'center',
      offsetX: 0,
      offsetY: 0,
    },
  };
}

export function createDefaultBlock(type: BlockType): DocumentBlock {
  const now = new Date().toISOString();
  switch (type) {
    case 'text':
      return {
        id: generateId('blk'),
        type: 'text',
        createdAt: now,
        data: { text: '' },
      } as TextBlock;

    case 'formula':
      return {
        id: generateId('blk'),
        type: 'formula',
        createdAt: now,
        data: {
          latex: 'E = m c^2',
          displayMode: true,
          equationNumber: '',
        },
      } as FormulaBlock;

    case 'table':
      return {
        id: generateId('blk'),
        type: 'table',
        createdAt: now,
        data: {
          rows: 3,
          cols: 3,
          hasHeaderRow: true,
          hasHeaderCol: false,
          borderStyle: 'academic',
          cells: [
            [
              { id: 'c00', content: 'Header 1', isHeader: true, bold: true, alignment: 'center' },
              { id: 'c01', content: 'Header 2', isHeader: true, bold: true, alignment: 'center' },
              { id: 'c02', content: 'Header 3', isHeader: true, bold: true, alignment: 'center' },
            ],
            [
              { id: 'c10', content: 'Data 1', alignment: 'center' },
              { id: 'c11', content: 'Data 2', alignment: 'center' },
              { id: 'c12', content: 'Data 3', alignment: 'center' },
            ],
            [
              { id: 'c20', content: 'Data 4', alignment: 'center' },
              { id: 'c21', content: 'Data 5', alignment: 'center' },
              { id: 'c22', content: 'Data 6', alignment: 'center' },
            ],
          ],
        },
      } as TableBlock;

    case 'image':
      return {
        id: generateId('blk'),
        type: 'image',
        createdAt: now,
        data: {
          assetId: '',
          caption: 'Figure: Diagram Description',
          altText: 'Laboratory Diagram',
          widthPercent: 80,
          alignment: 'center',
        },
      } as ImageBlock;

    case 'shape':
      return createShapeBlock('rectangle');

    case 'page_break':
      return {
        id: generateId('blk'),
        type: 'page_break',
        createdAt: now,
        data: { forced: true },
      } as PageBreakBlock;
  }
}

export function addBlock(
  doc: DocumentModel,
  sectionId: string,
  blockOrType: DocumentBlock | BlockType,
  targetIndex?: number
): DocumentModel {
  const block: DocumentBlock = typeof blockOrType === 'string'
    ? createDefaultBlock(blockOrType)
    : blockOrType;

  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;

      const blocks = [...sec.blocks];
      if (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex <= blocks.length) {
        blocks.splice(targetIndex, 0, block);
      } else {
        blocks.push(block);
      }
      return { ...sec, blocks };
    }),
  };
}

export function removeBlock(
  doc: DocumentModel,
  sectionId: string,
  blockId: string
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;
      return {
        ...sec,
        blocks: sec.blocks.filter((b) => b.id !== blockId),
      };
    }),
  };
}

export function updateBlockData<T extends DocumentBlock>(
  doc: DocumentModel,
  sectionId: string,
  blockId: string,
  dataUpdates: Partial<T['data']>
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;
      return {
        ...sec,
        blocks: sec.blocks.map((blk) => {
          if (blk.id !== blockId) return blk;
          return {
            ...blk,
            data: { ...blk.data, ...dataUpdates },
          } as DocumentBlock;
        }),
      };
    }),
  };
}

export function moveBlock(
  doc: DocumentModel,
  sectionId: string,
  blockId: string,
  direction: 'up' | 'down'
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;

      const index = sec.blocks.findIndex((b) => b.id === blockId);
      if (index === -1) return sec;

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= sec.blocks.length) return sec;

      const newBlocks = [...sec.blocks];
      const [removed] = newBlocks.splice(index, 1);
      newBlocks.splice(targetIndex, 0, removed);

      return { ...sec, blocks: newBlocks };
    }),
  };
}

export function reorderBlockLayer(
  doc: DocumentModel,
  sectionId: string,
  blockId: string,
  action: 'bringToFront' | 'sendToBack' | 'bringForward' | 'sendBackward'
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;

      const index = sec.blocks.findIndex((b) => b.id === blockId);
      if (index === -1) return sec;

      const newBlocks = [...sec.blocks];
      const [targetBlock] = newBlocks.splice(index, 1);

      if (action === 'bringToFront') {
        newBlocks.push(targetBlock);
      } else if (action === 'sendToBack') {
        newBlocks.unshift(targetBlock);
      } else if (action === 'bringForward') {
        const nextIdx = Math.min(newBlocks.length, index + 1);
        newBlocks.splice(nextIdx, 0, targetBlock);
      } else if (action === 'sendBackward') {
        const prevIdx = Math.max(0, index - 1);
        newBlocks.splice(prevIdx, 0, targetBlock);
      }

      return { ...sec, blocks: newBlocks };
    }),
  };
}

export function nudgeBlock(
  doc: DocumentModel,
  sectionId: string,
  blockId: string,
  dx: number,
  dy: number
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;
      return {
        ...sec,
        blocks: sec.blocks.map((blk) => {
          if (blk.id !== blockId) return blk;
          if (blk.type === 'text') {
            const formatting = blk.data.formatting || {};
            return {
              ...blk,
              data: {
                ...blk.data,
                formatting: {
                  ...formatting,
                  offsetX: (formatting.offsetX || 0) + dx,
                  offsetY: (formatting.offsetY || 0) + dy,
                },
              },
            } as DocumentBlock;
          }
          return {
            ...blk,
            data: {
              ...blk.data,
              offsetX: ((blk.data as any).offsetX || 0) + dx,
              offsetY: ((blk.data as any).offsetY || 0) + dy,
            },
          } as DocumentBlock;
        }),
      };
    }),
  };
}

export function duplicateBlock(
  doc: DocumentModel,
  sectionId: string,
  blockId: string
): DocumentModel {
  return {
    ...doc,
    metadata: { ...doc.metadata, modifiedAt: new Date().toISOString() },
    sections: doc.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;

      const index = sec.blocks.findIndex((b) => b.id === blockId);
      if (index === -1) return sec;

      const original = sec.blocks[index];
      const duplicated: DocumentBlock = {
        ...original,
        id: generateId('blk'),
        createdAt: new Date().toISOString(),
        data: JSON.parse(JSON.stringify(original.data)),
      };

      const newBlocks = [...sec.blocks];
      newBlocks.splice(index + 1, 0, duplicated);

      return { ...sec, blocks: newBlocks };
    }),
  };
}
